import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  X,
  Mic,
  Loader2,
  Clock,
  TrendingUp,
  Tag,
  ShoppingBag,
  Sparkles,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSettings } from "@/hooks/useSettings";
import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";

interface Product {
  id: string;
  name: string;
  price: number;
  image_url: string | null;
  category: string | null;
  on_sale: boolean | null;
  sale_price: number | null;
}

const RECENT_KEY = "rs_recent_searches";
const TRENDING = ["Saree", "Three-piece", "Kurti", "Jewelry", "Cotton", "Silk"];

const Highlight = ({ text, query }: { text: string; query: string }) => {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-primary/20 text-foreground rounded-sm px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
};

interface PremiumSearchBarProps {
  className?: string;
  placeholder?: string;
  variant?: "header" | "mobile";
}

export const PremiumSearchBar = ({
  className,
  placeholder = "Search for dresses, sarees, jewelry...",
  variant = "header",
}: PremiumSearchBarProps) => {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [activeCat, setActiveCat] = useState<string>("All");
  const [recent, setRecent] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [listening, setListening] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";

  // Load recent searches once
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (raw) setRecent(JSON.parse(raw));
    } catch {}
  }, []);

  // Lazy-load products + categories on first focus
  useEffect(() => {
    if (!focused || products.length) return;
    setLoading(true);
    supabase
      .from("products")
      .select("id,name,price,image_url,category,on_sale,sale_price")
      .then(({ data }) => {
        const list = (data as Product[]) ?? [];
        setProducts(list);
        const cats = Array.from(
          new Set(list.map((p) => p.category).filter(Boolean) as string[]),
        );
        setCategories(cats);
        setLoading(false);
      });
  }, [focused, products.length]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const saveRecent = (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    const next = [trimmed, ...recent.filter((r) => r.toLowerCase() !== trimmed.toLowerCase())].slice(0, 6);
    setRecent(next);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {}
  };

  const clearRecent = () => {
    setRecent([]);
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch {}
  };

  // Filter products by query + category
  const matched = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = products;
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.category?.toLowerCase().includes(q),
      );
    }
    if (activeCat !== "All") {
      list = list.filter((p) => p.category === activeCat);
    }
    return list.slice(0, 6);
  }, [query, products, activeCat]);

  const categoryMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return categories.filter((c) => c.toLowerCase().includes(q)).slice(0, 4);
  }, [query, categories]);

  const showResults = !!query.trim();

  // Build flat keyboard-navigable list
  const navItems = useMemo(() => {
    const items: Array<{ type: "product" | "category" | "recent" | "trending"; value: string; id?: string }> = [];
    if (showResults) {
      categoryMatches.forEach((c) => items.push({ type: "category", value: c }));
      matched.forEach((p) => items.push({ type: "product", value: p.name, id: p.id }));
    } else {
      recent.forEach((r) => items.push({ type: "recent", value: r }));
      TRENDING.forEach((t) => items.push({ type: "trending", value: t }));
    }
    return items;
  }, [showResults, categoryMatches, matched, recent]);

  useEffect(() => {
    setHighlightIdx(-1);
  }, [query, focused]);

  const goSearch = (q: string) => {
    if (!q.trim()) return;
    saveRecent(q);
    navigate(`/?q=${encodeURIComponent(q.trim())}`);
    setFocused(false);
    inputRef.current?.blur();
  };

  const goCategory = (c: string) => {
    saveRecent(c);
    navigate(`/?cat=${encodeURIComponent(c)}`);
    setFocused(false);
  };

  const goProduct = (id: string, name: string) => {
    saveRecent(query || name);
    navigate(`/product/${id}`);
    setFocused(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, navItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = navItems[highlightIdx];
      if (item) {
        if (item.type === "product" && item.id) goProduct(item.id, item.value);
        else if (item.type === "category") goCategory(item.value);
        else {
          setQuery(item.value);
          goSearch(item.value);
        }
      } else {
        goSearch(query);
      }
    } else if (e.key === "Escape") {
      setFocused(false);
      inputRef.current?.blur();
    }
  };

  // Voice search using Web Speech API (graceful degrade)
  const startVoice = () => {
    const SR =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;
    const recog = new SR();
    recog.lang = "en-US";
    recog.interimResults = false;
    recog.maxAlternatives = 1;
    setListening(true);
    recog.onresult = (e: any) => {
      const transcript = e.results[0][0].transcript;
      setQuery(transcript);
      setFocused(true);
      inputRef.current?.focus();
    };
    recog.onend = () => setListening(false);
    recog.onerror = () => setListening(false);
    recog.start();
  };

  const hasVoice =
    typeof window !== "undefined" &&
    ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);

  return (
    <div ref={wrapperRef} className={cn("relative w-full", className)}>
      {/* Search input — pill-shaped, soft peach background, glow on focus */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          goSearch(query);
        }}
        className={cn(
          "group relative flex items-center gap-2 rounded-full border transition-all duration-300",
          "bg-[hsl(20_60%_97%)] dark:bg-secondary/60 backdrop-blur-md",
          "border-border/60 shadow-soft",
          "hover:border-primary/40",
          focused && "border-primary/60 shadow-[0_0_0_4px_hsl(var(--primary)/0.08),0_8px_28px_-12px_hsl(var(--primary)/0.35)] bg-background",
          variant === "header" ? "h-11 pl-4 pr-2" : "h-12 pl-5 pr-2",
        )}
      >
        <Search
          className={cn(
            "h-4 w-4 shrink-0 transition-colors",
            focused ? "text-primary" : "text-muted-foreground",
          )}
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Search products"
          className="flex-1 bg-transparent text-sm font-sans outline-none placeholder:text-muted-foreground/70"
        />

        {loading && (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
        )}

        {query && !loading && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
            className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground transition-smooth"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}

        {hasVoice && !query && (
          <button
            type="button"
            onClick={startVoice}
            aria-label="Voice search"
            className={cn(
              "grid h-8 w-8 shrink-0 place-items-center rounded-full transition-smooth",
              listening
                ? "bg-primary text-primary-foreground animate-pulse"
                : "text-muted-foreground hover:bg-secondary hover:text-primary",
            )}
          >
            <Mic className="h-4 w-4" />
          </button>
        )}
      </form>

      {/* Dropdown */}
      {focused && (
        <div
          className={cn(
            "absolute left-0 right-0 top-full z-50 mt-2 origin-top overflow-hidden rounded-2xl border border-border/60",
            "bg-background/95 backdrop-blur-xl shadow-card animate-scale-in",
          )}
        >
          {/* Category chips */}
          {categories.length > 0 && (
            <div className="flex items-center gap-1.5 overflow-x-auto border-b border-border/50 px-3 py-2.5 scrollbar-none">
              <button
                type="button"
                onClick={() => setActiveCat("All")}
                className={cn(
                  "shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-smooth",
                  activeCat === "All"
                    ? "bg-foreground text-background"
                    : "bg-secondary/70 hover:bg-secondary",
                )}
              >
                All
              </button>
              {categories.slice(0, 6).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setActiveCat(c)}
                  className={cn(
                    "shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-smooth",
                    activeCat === c
                      ? "bg-foreground text-background"
                      : "bg-secondary/70 hover:bg-secondary",
                  )}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          <div className="max-h-[60vh] overflow-y-auto p-2">
            {/* Empty state: recent + trending */}
            {!showResults && (
              <div className="space-y-4 p-2">
                {recent.length > 0 && (
                  <div>
                    <div className="mb-2 flex items-center justify-between px-1">
                      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <Clock className="h-3 w-3" /> Recent
                      </p>
                      <button
                        type="button"
                        onClick={clearRecent}
                        className="text-[11px] text-muted-foreground hover:text-foreground"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {recent.map((r, i) => {
                        const flatIdx = i;
                        return (
                          <button
                            key={r}
                            type="button"
                            onMouseEnter={() => setHighlightIdx(flatIdx)}
                            onClick={() => {
                              setQuery(r);
                              goSearch(r);
                            }}
                            className={cn(
                              "rounded-full px-3 py-1.5 text-sm transition-smooth",
                              highlightIdx === flatIdx
                                ? "bg-primary/10 text-primary"
                                : "bg-secondary/70 hover:bg-secondary",
                            )}
                          >
                            {r}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div>
                  <p className="mb-2 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <TrendingUp className="h-3 w-3" /> Trending
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {TRENDING.map((t, i) => {
                      const flatIdx = recent.length + i;
                      return (
                        <button
                          key={t}
                          type="button"
                          onMouseEnter={() => setHighlightIdx(flatIdx)}
                          onClick={() => {
                            setQuery(t);
                            goSearch(t);
                          }}
                          className={cn(
                            "rounded-full border px-3 py-1.5 text-sm transition-smooth",
                            highlightIdx === flatIdx
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border bg-background hover:border-primary/50 hover:text-primary",
                          )}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Category suggestions */}
            {showResults && categoryMatches.length > 0 && (
              <div className="px-2 pt-1 pb-2">
                <p className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Tag className="h-3 w-3" /> Categories
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {categoryMatches.map((c, i) => (
                    <button
                      key={c}
                      type="button"
                      onMouseEnter={() => setHighlightIdx(i)}
                      onClick={() => goCategory(c)}
                      className={cn(
                        "rounded-full px-3 py-1 text-xs font-medium transition-smooth",
                        highlightIdx === i
                          ? "bg-primary/15 text-primary"
                          : "bg-primary/10 text-primary hover:bg-primary/15",
                      )}
                    >
                      <Highlight text={c} query={query} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No results */}
            {showResults && matched.length === 0 && !loading && (
              <div className="p-6 text-center">
                <Sparkles className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
                <p className="text-sm font-medium">
                  No products match &ldquo;{query}&rdquo;
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Try a different keyword or browse trending.
                </p>
              </div>
            )}

            {/* Product results */}
            {showResults && matched.length > 0 && (
              <div className="px-1 pt-1">
                <p className="mb-1 px-3 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Products
                </p>
                {matched.map((p, i) => {
                  const flatIdx = categoryMatches.length + i;
                  const price =
                    p.on_sale && p.sale_price ? Number(p.sale_price) : Number(p.price);
                  return (
                    <Link
                      key={p.id}
                      to={`/product/${p.id}`}
                      onMouseEnter={() => setHighlightIdx(flatIdx)}
                      onClick={() => {
                        saveRecent(query || p.name);
                        setFocused(false);
                      }}
                      className={cn(
                        "flex items-center gap-3 rounded-xl p-2 transition-smooth",
                        highlightIdx === flatIdx ? "bg-secondary" : "hover:bg-secondary/70",
                      )}
                    >
                      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-secondary">
                        {p.image_url ? (
                          <img
                            src={p.image_url}
                            alt={p.name}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="grid h-full w-full place-items-center">
                            <ShoppingBag className="h-4 w-4 text-muted-foreground/40" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          <Highlight text={p.name} query={query} />
                        </p>
                        {p.category && (
                          <p className="truncate text-xs text-muted-foreground">
                            <Highlight text={p.category} query={query} />
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        {p.on_sale && p.sale_price ? (
                          <>
                            <span className="block font-display text-sm font-semibold text-destructive">
                              {formatCurrency(price, symbol)}
                            </span>
                            <span className="text-[10px] text-muted-foreground line-through">
                              {formatCurrency(Number(p.price), symbol)}
                            </span>
                          </>
                        ) : (
                          <span className="font-display text-sm font-semibold">
                            {formatCurrency(price, symbol)}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
                <button
                  type="button"
                  onClick={() => goSearch(query)}
                  className="mt-1 w-full rounded-xl p-2.5 text-center text-sm font-medium text-primary hover:bg-secondary transition-smooth"
                >
                  See all results for &ldquo;{query}&rdquo; →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
